import { z } from 'zod';

import { defineNode, passThrough, total } from '../define';
import { PLATFORMS } from '../types';

export const brandGuardrails = defineNode({
  type: 'guardrail.brandGuardrails',
  version: 1,
  category: 'guardrail',
  label: 'Brand guardrails',
  description:
    'Check claims and tone against your brand profile; block or flag. Skipped while you have no approved claims or prohibited language.',
  icon: 'SealCheck',
  status: 'ga',
  // Scripts too, so claims can be checked before paying for renders.
  inputs: [{ id: 'in', accepts: ['script', 'reel', 'carousel'] }],
  outputs: [{ id: 'out', type: 'same' }],
  mode: 'map',
  executor: 'llm',
  bounded: true,
  config: z.object({
    onViolation: z.enum(['flag', 'block']).default('flag').meta({
      title: 'When something breaks a rule',
      description: 'Flag holds it for a fix; block drops it',
      widget: 'segmented',
    }),
  }),
  cardinality: passThrough,
});

export const platformPolicy = defineNode({
  type: 'guardrail.platformPolicy',
  version: 1,
  category: 'guardrail',
  label: 'Platform policy',
  description: 'Music rights, banned terms and platform rules.',
  icon: 'ShieldWarning',
  status: 'ga',
  inputs: [{ id: 'in', accepts: ['reel'] }],
  outputs: [{ id: 'out', type: 'reel' }],
  mode: 'map',
  executor: 'llm',
  bounded: true,
  config: z.object({
    platforms: z.array(z.enum(PLATFORMS)).default([]).meta({
      title: 'Check against',
      description: 'Empty means every platform you publish to',
      widget: 'chips',
    }),
  }),
  cardinality: passThrough,
});

export const humanApproval = defineNode({
  type: 'guardrail.humanApproval',
  version: 1,
  category: 'guardrail',
  label: 'Human approval',
  description: 'Send to the review queue; a reviewer approves before it continues.',
  icon: 'UserCheck',
  status: 'ga',
  inputs: [{ id: 'in', accepts: 'any' }],
  outputs: [{ id: 'out', type: 'same' }],
  mode: 'map',
  executor: 'control',
  bounded: true,
  config: z.object({
    /** A workspace member; empty lets any owner or admin review. */
    reviewerId: z.uuid().optional().meta({ title: 'Reviewer', widget: 'member' }),
    timeoutHours: z.int().min(1).max(720).optional().meta({
      title: 'Reject if not reviewed within (h)',
    }),
  }),
  cardinality: passThrough,
});

export const BRANCH_FIELDS = [
  'format',
  'platform',
  'durationSeconds',
  'views',
  'language',
] as const;

export const branch = defineNode({
  type: 'guardrail.branch',
  version: 1,
  category: 'guardrail',
  label: 'Branch',
  description: 'Route items down different paths with an if / else rule.',
  icon: 'GitBranch',
  status: 'ga',
  inputs: [{ id: 'in', accepts: 'any' }],
  outputs: [
    { id: 'match', label: 'If', type: 'same' },
    { id: 'else', label: 'Else', type: 'same' },
  ],
  mode: 'map',
  executor: 'control',
  bounded: true,
  config: z.object({
    field: z.enum(BRANCH_FIELDS).default('format').meta({ title: 'When' }),
    operator: z
      .enum(['is', 'isNot', 'above', 'below'])
      .default('is')
      .meta({ title: 'Rule' }),
    value: z
      .union([z.string().trim().min(1).max(100), z.number()])
      .default('reel')
      .meta({
        title: 'Value',
      }),
  }),
  // Every item takes one path; either could get them all.
  cardinality: (_config, inputs) => ({ match: total(inputs), else: total(inputs) }),
});

export const delay = defineNode({
  type: 'guardrail.delay',
  version: 1,
  category: 'guardrail',
  label: 'Delay',
  description: 'Wait before the next step — e.g. 48 h before Pick winner.',
  icon: 'Hourglass',
  status: 'ga',
  inputs: [{ id: 'in', accepts: 'any' }],
  outputs: [{ id: 'out', type: 'same' }],
  mode: 'map',
  executor: 'control',
  config: z.object({
    hours: z.number().min(0.25).max(720).default(48).meta({ title: 'Wait (h)' }),
  }),
  cardinality: passThrough,
});

export const GUARDRAILS = [brandGuardrails, platformPolicy, humanApproval, branch, delay];
